import { Button } from "@/components/ui/button";
import { effectiveOptionMap } from "@/utils/field-options";
import { Form } from "@/components/ui/form";
import { useDynamicOptions } from "@/hooks/use-dynamic-options";
import { FieldDataType, FieldValidator, RelatedListColumnConfig, TabContentResponse } from "@/types/dynamic-detail";
import { buildZodSchema } from "@/utils/dynamic-form";
import { renderField } from "@/utils/field-renderer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useFetcher } from "react-router-dom";
import { toast } from "sonner";
import { DynamicFormField } from "./dynamic-form-field";
import { EditableRelatedList, EditableRelatedListColumn, EditableRelatedListFormProps } from "./editable-related-list";

// ---------------------------------------------------------------------------
// Context passed from DynamicRelatedListContent to DynamicRelatedListForm
// ---------------------------------------------------------------------------

type RelatedListItem = Record<string, any> & { id: string | number };

type RelatedListFormContext = {
    columns: RelatedListColumnConfig[];
    items: Record<string, any>[];
    childObjectUrl: string;
    parentIdField: string | null;
    objectId: string;
    onMutate?: () => void;
};

function getColumnKey(col: RelatedListColumnConfig): string {
    return col.field_name || col.key || "";
}

function getValidator(col: RelatedListColumnConfig, type: FieldValidator["type"]): FieldValidator | undefined {
    return col.validators?.find(validator => validator.type === type);
}

function isRequiredColumn(col: RelatedListColumnConfig): boolean {
    return col.required || !!getValidator(col, "required");
}

/** Empty value a column starts from on create, matching what its zod schema accepts. */
function emptyValueForColumn(col: RelatedListColumnConfig): unknown {
    switch (col.field_type) {
        case "number":
        case "percentage":
            return undefined;
        case "checkbox":
        case "boolean":
            return false;
        default:
            return "";
    }
}

// ---------------------------------------------------------------------------
// DynamicRelatedListForm — generic form driven by column config
// ---------------------------------------------------------------------------

/** Map related list column to FieldDataType-like for DynamicFormField and useDynamicOptions */
function columnToFieldDataType(col: RelatedListColumnConfig, item: RelatedListItem | undefined): FieldDataType {
    const columnKey = getColumnKey(col);
    const rawType = (col.field_type || "text") as string;
    return {
        field_name: columnKey,
        label: col.label,
        value: item?.[columnKey],
        href: null,
        field_type: rawType === "fk" ? "select" : rawType,
        large_field: (col as any).large_field ?? false,
        required: isRequiredColumn(col),
        validators: col.validators ?? undefined,
        options: (col as any).options,
        options_url: (col as any).options_url,
        description: (col as any).description,
        disabled: (col as any).disabled
    };
}

function DynamicRelatedListForm({ item, setOpen, context }: EditableRelatedListFormProps<RelatedListItem, RelatedListFormContext>) {
    const isEdit = item !== undefined;
    const fetcher = useFetcher();

    // Memoize formColumns to stabilise schema/defaultValues dependencies
    const formColumns = useMemo(() => context.columns.filter(col => col.editable !== false), [context.columns]);

    // Fields for useDynamicOptions
    const fieldsForOptions = useMemo(() => formColumns.map(col => columnToFieldDataType(col, item)), [formColumns, item]);
    const { dynamicOptions, loadingOptions, failedOptions } = useDynamicOptions(fieldsForOptions);
    // Effective options (`resolveFieldOptions`): a served list wins over a fetched one.
    const fieldOptions = useMemo(() => effectiveOptionMap(fieldsForOptions, { dynamicOptions }), [fieldsForOptions, dynamicOptions]);

    const schema = useMemo(
        () =>
            buildZodSchema(fieldsForOptions, {
                mapFieldSchema: ({ field, schema: baseSchema }) => {
                    const uniqueValidator = field.validators?.find(validator => validator.type === "unique_in_relation");
                    if (!uniqueValidator) return baseSchema;
                    if (field.field_type !== "number" && field.field_type !== "duration" && field.field_type !== "percentage") return baseSchema;

                    const fieldName = field.field_name;
                    const currentValue = item?.[fieldName];
                    const message = uniqueValidator.message || "Cette valeur est déjà utilisée";
                    return baseSchema.refine(
                        value => value == null || value === "" || Number(value) === Number(currentValue) || !context.items.some(existing => Number(existing[fieldName]) === Number(value)),
                        message
                    );
                }
            }),
        [fieldsForOptions, item, context.items]
    );

    const defaultValues = useMemo(() => {
        const vals: Record<string, any> = {};
        for (const col of formColumns) {
            const columnKey = getColumnKey(col);
            let value = item?.[columnKey] ?? emptyValueForColumn(col);
            if (typeof value === "object") {
                value = value.raw_value ?? value.value;
            }
            vals[columnKey] = value;
        }
        return vals;
    }, [formColumns, item]);

    const form = useForm({ resolver: zodResolver(schema), defaultValues });
    // Guard against stale fetcher.data causing the effect to fire on remount
    const processedDataRef = useRef<unknown>(null);

    const onSubmit = (values: Record<string, any>) => {
        const payload: Record<string, any> = { ...values };

        if (isEdit) {
            fetcher.submit(
                {
                    __childObjectUrl: context.childObjectUrl,
                    id: String(item.id),
                    ...payload
                },
                { method: "post", encType: "application/json", action: "?action=related-list-update" }
            );
        } else {
            fetcher.submit(
                {
                    __childObjectUrl: context.childObjectUrl,
                    __parentIdField: context.parentIdField ?? "",
                    __parentId: context.objectId,
                    ...payload
                },
                { method: "post", encType: "application/json", action: "?action=related-list-create" }
            );
        }
    };

    useEffect(() => {
        if (!fetcher.data || fetcher.data === processedDataRef.current) return;
        processedDataRef.current = fetcher.data;
        if ((fetcher.data as { success?: boolean }).success) {
            toast.success(isEdit ? "Modifié avec succès" : "Créé avec succès");
            setOpen(false);
            context.onMutate?.();
        } else {
            toast.error((fetcher.data as { message?: string }).message ?? "Une erreur est survenue");
        }
    }, [fetcher.data, isEdit, setOpen, context.onMutate]);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                    {formColumns.map(col => {
                        const columnKey = getColumnKey(col);
                        const fieldData = columnToFieldDataType(col, item);
                        return (
                            <div className={`col-span-${col.large_field ? 2 : 1}`}>
                                <DynamicFormField
                                    key={columnKey}
                                    field={fieldData}
                                    form={form}
                                    dynamicOptions={fieldOptions}
                                    loadingOptions={loadingOptions}
                                    failedOptions={failedOptions}
                                    backendFieldErrors={{}}
                                    contextName="related-list"
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" ? "Enregistrement..." : isEdit ? "Sauvegarder" : "Créer"}
                    </Button>
                </div>
            </form>
        </Form>
    );
}

// ---------------------------------------------------------------------------
// Cell renderer for the table
// ---------------------------------------------------------------------------

function renderCell(col: RelatedListColumnConfig, item: Record<string, any>): React.ReactNode {
    const columnKey = getColumnKey(col);
    const value = item[columnKey];
    const field: FieldDataType = {
        field_name: columnKey,
        label: col.label,
        value,
        href: null,
        field_type: col.field_type,
        large_field: false,
        required: isRequiredColumn(col),
        validators: col.validators
    };
    // Field actions stay on: a contact's phone or email listed on an account must be callable
    // and mailable from the row, like the same field in a header or a section (Freshdesk #78854).
    return renderField(field, { layout: "flex", showLabel: false });
}

// ---------------------------------------------------------------------------
// DynamicRelatedListContent — main export
// ---------------------------------------------------------------------------

type DynamicRelatedListContentProps = {
    content: TabContentResponse;
    objectId: string | number;
    onMutate?: () => void;
};

export function DynamicRelatedListContent({ content, objectId, onMutate }: DynamicRelatedListContentProps) {
    const columns = useMemo(
        () =>
            (content.columns ?? []).map(col => ({
                ...col,
                key: col.key ?? col.field_name
            })),
        [content.columns]
    );
    const items = content.items ?? [];
    const childObjectUrl = content.child_object_url ?? "";

    const tableColumns: EditableRelatedListColumn<RelatedListItem>[] = columns.map(col => ({
        name: col.label,
        accessorFn: (item: RelatedListItem) => renderCell(col, item),
        size: col.size ?? undefined
    }));

    const formContext: RelatedListFormContext = {
        columns,
        items,
        childObjectUrl,
        parentIdField: content.parent_id_field ?? null,
        objectId: String(objectId),
        onMutate
    };

    const extraDeleteData: Record<string, string> = { __childObjectUrl: childObjectUrl };

    return (
        <div className="space-y-4">
            <EditableRelatedList<RelatedListItem, RelatedListFormContext>
                title={content.title ?? ""}
                description={content.description ?? undefined}
                items={items as RelatedListItem[]}
                columns={tableColumns}
                formComponent={DynamicRelatedListForm}
                context={formContext}
                actionPrefix="related-list"
                extraDeleteData={Object.keys(extraDeleteData).length > 0 ? extraDeleteData : undefined}
                onDeleteSuccess={onMutate}
                itemToId={item => String(item.id)}
                createDialogTitle={content.title ? `Ajouter — ${content.title}` : "Ajouter"}
                editDialogTitle={content.title ? `Modifier — ${content.title}` : "Modifier"}
                permissions={content.permissions}
                emptyMessage={content.empty_message ?? "Aucun élément"}
            />
        </div>
    );
}
